import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { borderPrimary, rounded, semiBoldText, whiteBG, primaryText, largeTextSize, smallTextSize } from '../../styling/stylingHelper';
import { CustomDatePicker } from './CustomDatePicker';
import { normalizeDate } from '../../utils/dateUtils';

export type TimeViewOption = 'all' | 'day' | 'week' | 'month' | 'quarter';

interface TimeControlsProps {
  startingDate: Date;
  selectedTimeOption: TimeViewOption;
  dateRange?: { start: Date; end: Date } | null;
  onChange?: (startDate: Date, endDate: Date) => void;
  className?: string;
  align?: 'left' | 'right' | 'center';
}

const formatDate = (date: Date, options: Intl.DateTimeFormatOptions) => {
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

export const TimeControls: React.FC<TimeControlsProps> = ({
  startingDate,
  selectedTimeOption,
  dateRange,
  onChange,
  className,
  align = 'right',
}) => {
  const getPeriod = (option: TimeViewOption, refDate: Date) => {
    let start: Date, end: Date;
    switch (option) {
      case 'day':
        start = new Date(refDate);
        end = new Date(refDate);
        break;
      case 'week': {
        const dayOfWeek = refDate.getDay();
        const diff = refDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start = new Date(refDate);
        start.setDate(diff);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        break;
      }
      case 'month':
        start = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
        end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        break;
      case 'quarter': {
        const currentQuarter = Math.floor(refDate.getMonth() / 3);
        const startMonth = currentQuarter * 3;
        start = new Date(refDate.getFullYear(), startMonth, 1);
        end = new Date(refDate.getFullYear(), startMonth + 3, 0);
        break;
      }
      default:
        start = new Date('2025-01-01');
        end = new Date();
    }
    return { start, end };
  };

  const currentPeriod = getPeriod(selectedTimeOption, startingDate);

  let displayText = '';
  let isCustom = false;

  switch (selectedTimeOption) {
    case 'all':
      isCustom = true;
      if (dateRange) {
        const startStr = formatDate(dateRange.start, { month: 'short', day: 'numeric' });
        const endStr = formatDate(dateRange.end, { month: 'short', day: 'numeric', year: 'numeric' });
        displayText = `${startStr} – ${endStr}`;
      } else {
        displayText = 'Select custom dates';
      }
      break;
    case 'day':
      // Custom format: Mon - Sept. 4 2025 (no comma)
      {
        const date = currentPeriod.start;
        const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
        const month = date.toLocaleDateString(undefined, { month: 'short' });
        const day = date.getDate();
        const year = date.getFullYear();
        displayText = `${weekday} – ${month}. ${day}, ${year}`;
      }
      break;
    case 'week': {
      const startShort = formatDate(currentPeriod.start, { month: 'short', day: 'numeric' });
      const endShort = formatDate(currentPeriod.end, { month: 'short', day: 'numeric', year: 'numeric' });
      const getWeekNumber = (date: Date) => {
        const target = new Date(date.valueOf());
        const dayNr = (date.getDay() + 6) % 7;
        target.setDate(target.getDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setMonth(0, 1);
        if (target.getDay() !== 4) {
          target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
        }
        return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
      };
      const weekNumber = getWeekNumber(currentPeriod.start);
      displayText = `W${weekNumber} – ${startShort} - ${endShort}`;
      break;
    }
    case 'month':
      displayText = formatDate(currentPeriod.start, { month: 'long', year: 'numeric' });
      break;
    case 'quarter': {
      const quarter = Math.floor(currentPeriod.start.getMonth() / 3) + 1;
      const year = currentPeriod.start.getFullYear();
      displayText = `Q${quarter} ${year}`;
      break;
    }
  }

  const handlePrev = () => {
    if (!onChange) return;
    let newRefDate: Date;
    switch (selectedTimeOption) {
      case 'day':
        newRefDate = new Date(startingDate);
        newRefDate.setDate(newRefDate.getDate() - 1);
        break;
      case 'week': {
        const current = getPeriod(selectedTimeOption, startingDate);
        newRefDate = new Date(current.start);
        newRefDate.setDate(newRefDate.getDate() - 7);
        break;
      }
      case 'month': {
        const current = getPeriod(selectedTimeOption, startingDate);
        newRefDate = new Date(current.start.getFullYear(), current.start.getMonth() - 1, 1);
        break;
      }
      case 'quarter': {
        const current = getPeriod(selectedTimeOption, startingDate);
        newRefDate = new Date(current.start.getFullYear(), current.start.getMonth() - 3, 1);
        break;
      }
      default:
        return;
    }
    const newPeriod = getPeriod(selectedTimeOption, newRefDate);
    onChange(newPeriod.start, newPeriod.end);
  };

  const handleNext = () => {
    if (!onChange) return;
    let newRefDate: Date;
    switch (selectedTimeOption) {
      case 'day':
        newRefDate = new Date(startingDate);
        newRefDate.setDate(newRefDate.getDate() + 1);
        break;
      case 'week': {
        const current = getPeriod(selectedTimeOption, startingDate);
        newRefDate = new Date(current.start);
        newRefDate.setDate(newRefDate.getDate() + 7);
        break;
      }
      case 'month': {
        const current = getPeriod(selectedTimeOption, startingDate);
        newRefDate = new Date(current.start.getFullYear(), current.start.getMonth() + 1, 1);
        break;
      }
      case 'quarter': {
        const current = getPeriod(selectedTimeOption, startingDate);
        newRefDate = new Date(current.start.getFullYear(), current.start.getMonth() + 3, 1);
        break;
      }
      default:
        return;
    }
    const newPeriod = getPeriod(selectedTimeOption, newRefDate);

    // Don't allow going into the future
    const today = normalizeDate(new Date());
    if (newPeriod.start > today) {
      return;
    }

    onChange(newPeriod.start, newPeriod.end);
  };

  // Check if next button should be disabled
  const isNextDisabled = () => {
    const today = normalizeDate(new Date());
    let nextRefDate: Date;

    switch (selectedTimeOption) {
      case 'day':
        nextRefDate = new Date(startingDate);
        nextRefDate.setDate(nextRefDate.getDate() + 1);
        break;
      case 'week': {
        const current = getPeriod(selectedTimeOption, startingDate);
        nextRefDate = new Date(current.start);
        nextRefDate.setDate(nextRefDate.getDate() + 7);
        break;
      }
      case 'month': {
        const current = getPeriod(selectedTimeOption, startingDate);
        nextRefDate = new Date(current.start.getFullYear(), current.start.getMonth() + 1, 1);
        break;
      }
      case 'quarter': {
        const current = getPeriod(selectedTimeOption, startingDate);
        nextRefDate = new Date(current.start.getFullYear(), current.start.getMonth() + 3, 1);
        break;
      }
      default:
        return true;
    }

    const nextPeriod = getPeriod(selectedTimeOption, nextRefDate);
    return nextPeriod.start > today;
  };

  // Custom date range dropdown state
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const closeDropdown = () => setIsOpen(false);

  const handleApply = (startDate: Date, endDate: Date) => {
    if (onChange) {
      onChange(startDate, endDate);
    }
    closeDropdown();
  };

  const handleCancel = () => {
    closeDropdown();
  };

  const renderCustomButton = () => (
    <button
      onClick={handleToggle}
      className={cn("p-2", "border", borderPrimary, whiteBG, rounded, primaryText, "flex items-center gap-2", "max-[550px]:w-full max-[550px]:justify-center")}
      type="button"
    >
      <Calendar className="w-4 h-4" />
      {/* Was an inline style={{ color: '#667085' }} — inline styles are unreachable by `dark:`,
          so the label stayed mid-grey on the dark canvas. Same light hex, paired for dark. */}
      <span className={cn(semiBoldText, smallTextSize, "text-[#667085] dark:text-textGrey-dark")}>
        {displayText}
      </span>
    </button>
  );

  return (
    <div className={cn("relative", className)}>
      {isCustom ? (
        <>
          {renderCustomButton()}
          {isOpen && (
            <CustomDatePicker
              initialStartDate={dateRange ? normalizeDate(dateRange.start) : normalizeDate(new Date())}
              initialEndDate={dateRange ? normalizeDate(dateRange.end) : normalizeDate(new Date())}
              onApply={handleApply}
              onCancel={handleCancel}
              onClose={closeDropdown}
              align={align}
            />
          )}
        </>
      ) : (
        // Responsive: above 650px as before, below 650px button/label/button flexed edge-to-edge
        <div
          className={cn(
            "flex items-center",
            "max-[650px]:w-full max-[650px]:flex max-[650px]:justify-between"
          )}
        >
          <button
            data-onboarding="time-previous"
            onClick={handlePrev}
            className={cn(
              "p-2 border",
              borderPrimary,
              rounded,
              whiteBG,
              primaryText,
              // Make prev/next full height on mobile, float to edge
              "max-[650px]:flex-shrink-0"
            )}
            type="button"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span
            className={cn(
              primaryText,
              largeTextSize,
              semiBoldText,
              "px-2 text-center",
              // Grow the middle label on mobile to fill space and center text
              "max-[650px]:flex-1 max-[650px]:mx-2"
            )}
            style={{ minWidth: 0 }}
          >
            {displayText}
          </span>
          <button
            onClick={handleNext}
            disabled={isNextDisabled()}
            className={cn(
              "p-2 border",
              borderPrimary,
              rounded,
              whiteBG,
              primaryText,
              // Make prev/next full height on mobile, float to edge
              "max-[650px]:flex-shrink-0",
              isNextDisabled() && "opacity-50"
            )}
            type="button"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};